import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/* ================= TYPES ================= */

export type CartItem = {
  productId: string;
  ownerId: string;
  name: string;
  price: number;        // EUR, per unit
  lineTotal?: number;   // price * qty, re-derived on every change
  photo?: string;
  qty: number;
  stock?: number;       // caps qty
  unit?: string;
  categoryId?: number;
};
export type AddToCartPayload = Omit<CartItem, "qty"> & { qty?: number };

export type VariantKey = { productId: string };

export type CartState = {
  userId: string | null;
  items: CartItem[];
};

/* ================= STATE ================= */

const KEY = "cart";
const loadItems = (): CartItem[] => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};
export const persistCart = (items: CartItem[]) => {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch {}
};

const initialState: CartState = { userId: null, items: loadItems() };

const line = (i: CartItem) => +(i.price * i.qty).toFixed(2);

/* ================= SLICE ================= */

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCartUser: (state, action: PayloadAction<string | null>) => { state.userId = action.payload; },

    /* ===== ADD TO CART ===== */
    addToCart: (state, action: PayloadAction<AddToCartPayload>) => {
      const qtyToAdd = action.payload.qty ?? 1;
      const existing = state.items.find((i) => i.productId === action.payload.productId);
      if (existing) {
        const cap = existing.stock ?? Infinity;
        existing.qty = Math.min(existing.qty + qtyToAdd, cap);
        existing.lineTotal = line(existing);
      } else {
        const { qty, ...rest } = action.payload;
        const cap = rest.stock ?? Infinity;
        const item: CartItem = { ...rest, qty: Math.min(qtyToAdd, cap) };
        item.lineTotal = line(item);
        if (item.qty > 0) state.items.push(item);
      }
    },

    /* ===== DECREMENT ===== */
    decrementCartItem: (state, action: PayloadAction<VariantKey>) => {
      const item = state.items.find((i) => i.productId === action.payload.productId);
      if (!item) return;
      if (item.qty > 1) {
        item.qty -= 1;
        item.lineTotal = line(item);
      } else {
        state.items = state.items.filter((i) => i.productId !== action.payload.productId);
      }
    },

    /* ===== REMOVE ===== */
    removeFromCart: (state, action: PayloadAction<VariantKey>) => {
      state.items = state.items.filter((i) => i.productId !== action.payload.productId);
    },

    clearCart: (state) => { state.items = []; },
  },
});

export const { setCartUser, addToCart, decrementCartItem, removeFromCart, clearCart } = cartSlice.actions;

export const cartCount = (items: CartItem[]) => items.reduce((n, i) => n + i.qty, 0);
export const cartSubtotal = (items: CartItem[]) => +items.reduce((t, i) => t + i.price * i.qty, 0).toFixed(2);

export default cartSlice.reducer;
